<template>
    <div class="overview-container">
        <div v-if="getCurrentViberCampaign" class="info-container">
            <v-card class="table-wrapper">
                <campaign-table :rows="campaignTableRows">
                    <template v-slot:fallback_icon>
                        <two-state-icon :condition="getCurrentViberCampaign.fallback ?? false" class="fallback-icon" />
                    </template>
                    <template v-slot:file_used>
                        <v-btn
                            color="primary"
                            class="my-2 white--text"
                            @click="downloadFile"
                        >
                            Download
                            <v-icon right dark> mdi-file-download-outline </v-icon>
                        </v-btn>
                    </template>
                </campaign-table>
                <create-form-actions
                    needs-padding
                    has-submit-btn
                    :submit-loading="submitLoading"
                    :are-buttons-disabled="submitLoading"
                    has-previous-step-btn
                    @previous-step="emitPrevStep"
                    @cancel="emitCancel"
                    @reset="emitReset"
                    @submit-campaign="submitCampaign"
                />
            </v-card>
        </div>
        <v-card v-if="getCurrentViberCampaign.file" class="table-container">
            <div class="card-title">{{ viberTitle }}</div>
            <array-view
                :title="viberTitle"
                :items="viberMessages"
                :headers="viberHeaders"
                :total-pages="viberMessagesTotalPages"
                :page="viberPage"
                :size="viberSize"
                :total-items="totalViberMessages"
                :has-search-field="true"
                :update-filter="updateViberFilter"
                :is-fetching="isFetchingViber"
                should-rows-expand
                @paginationChange="viberPaginationChange"
            />
        </v-card>
        <v-card
            v-if="getCurrentViberCampaign.fallback && getCurrentViberCampaign.file"
            class="table-container"
        >
            <div class="card-title">{{ fallbackTitle }}</div>

            <array-view
                :title="fallbackTitle"
                :items="fallbackMessages"
                :headers="fallbackHeaders"
                :total-pages="fallbackMessagesTotalPages"
                :page="fallbackPage"
                :size="fallbackSize"
                :has-search-field="true"
                :update-filter="updateFallbackFilter"
                :total-items="totalFallbackMessages"
                :is-fetching="isFetchingFallback"
                should-rows-expand
                @paginationChange="fallbackPaginationChange"
            />
        </v-card>
        <div id="export" />
    </div>
</template>

<script>
    import dateTimeUtils from '@/utils/date-time-utils'
    import viberCampaignService from '@/services/viber-campaign-service'
    import ArrayView from '@/components/ArrayView.vue'
    import CreateFormActions from '@/components/CreateFormActions.vue'
    import CampaignTable from '@/components/CampaignTable.vue'
    import TwoStateIcon from '@/components/TwoStateIcon.vue'
    import tableUtils from '@/utils/tableUtils'
    export default {
        components: { TwoStateIcon, CampaignTable, CreateFormActions, ArrayView },
        data() {
            return {
                submitLoading: false,
                viberTitle: 'Viber Messages List',
                fallbackTitle: 'SMS Fallback List',
                viberHeaders: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'viberText' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                fallbackHeaders: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'smsText' },
                    { text: 'SMS Parts', value: 'parts' },
                    { text: 'Chars', value: 'chars' },
                    { text: 'Is UTF-8', value: 'utf8' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                viberMessages: [],
                fallbackMessages: [],
                viberMessagesTotalPages: 0,
                fallbackMessagesTotalPages: 0,
                viberSize: 5,
                viberPage: 1,
                fallbackSize: 5,
                fallbackPage: 1,
                totalViberMessages: 0,
                totalFallbackMessages: 0,
                viberFilter: '',
                fallbackFilter: '',
                isFetchingViber: false,
                isFetchingFallback: false
            }
        },
        computed: {
            campaignTableRows() {
                return [
                    {
                        header: 'Campaign :',
                        value: this.getCurrentViberCampaign.alias
                    },
                    {
                        header: 'Originator :',
                        value: this.getCurrentViberCampaign.originator
                    },
                    {
                        header: 'Text Template :',
                        value: this.getCurrentViberCampaign.textTemplate
                    },
                    {
                        header: 'Alias :',
                        value: this.getCurrentViberCampaign.alias
                    },
                    {
                        header: 'TTL :',
                        value: this.getCurrentViberCampaign.ttl
                    },
                    {
                        header: 'SMS Fallback :',
                        type: 'slot',
                        slot: 'fallback_icon'
                    },
                    {
                        header: 'SMS Fallback Text Template :',
                        value: this.getCurrentViberCampaign.fallbackTextTemplate ?? '-'
                    },
                    {
                        header: 'Button text :',
                        value: this.getCurrentViberCampaign.buttonCaption ?? '-'
                    },
                    {
                        header: 'Button Url :',
                        value: this.getCurrentViberCampaign.buttonUrl ?? '-'
                    },
                    {
                        header: 'Image Url :',
                        value: this.getCurrentViberCampaign.imageUrl ?? '-'
                    },
                    {
                        header: 'File Used :',
                        type: 'slot',
                        renderCondition: !!this.getCurrentViberCampaign.file,
                        slot: 'file_used'
                    },
                    {
                        header: 'Created At :',
                        value: this.getHumanizedDateTime(this.getCurrentViberCampaign.createdAt)
                    },
                    {
                        header: 'Scheduled At :',
                        value: this.getHumanizedDateTime(this.getCurrentViberCampaign.scheduledAt)
                    },
                    {
                        header: 'Numbers :',
                        value: this.totalViberMessages
                    },
                    {
                        header: 'Parameters :',
                        value: this.getCurrentViberCampaign.params
                    }
                ]
            },
            getCurrentViberCampaign() {
                return this.$store.getters.getCurrentViberCampaign
            },
            filter() {
                return {
                    filter: { q: this.viberFilter },
                    range: [this.viberPage - 1, this.viberSize]
                }
            },
            fallbackMessagesFilter() {
                return {
                    filter: { q: this.fallbackFilter },
                    range: [this.fallbackPage - 1, this.fallbackSize]
                }
            }
        },
        watch: {
            getCurrentViberCampaign() {
                this.initDetails()
            },
            filter() {
                this.fetchAllViberMessages()
            },
            fallbackMessagesFilter() {
                this.fetchAllFallbackMessages()
            }
        },
        created() {
            this.initDetails()
        },
        methods: {
            async   initDetails() {
                if (this.getCurrentViberCampaign.id) {
                    await new Promise(resolve => setTimeout(resolve, 500))
                    await this.fetchAllViberMessages()
                    await this.fetchAllFallbackMessages()
                }
            },
            getHumanizedDateTime(dateTime) {
                return dateTimeUtils.getHumanizedDateTime(dateTime)
            },
            submitCampaign() {
                this.submitLoading = true
                return viberCampaignService
                    .submitViberCampaign(this.getCurrentViberCampaign.id)
                    .then(() => {
                        this.$emit('set-alert', 'Campaign Created', 'success')
                        setTimeout(() => {
                            this.submitLoading = false
                            this.$router.push(`/viber/${this.getCurrentViberCampaign.id}`)
                        }, 3000)
                    }).catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
            },
            emitPrevStep() {
                this.$emit('prev-step')
            },
            emitCancel() {
                this.$emit('cancel')
            },
            emitReset() {
                this.$emit('reset')
            },
            viberPaginationChange(page, size) {
                this.viberSize = size
                this.viberPage = page
            },
            fallbackPaginationChange(page, size) {
                this.fallbackSize = size
                this.fallbackPage = page
            },
            fetchAllViberMessages() {
                if (this.isFetchingViber) return
                this.isFetchingViber = true
                return viberCampaignService
                    .getViberMessages(this.getCurrentViberCampaign.id, this.filter, true)
                    .then((resp) => {
                        this.viberMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.viberPage - 1) * this.viberSize + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.viberMessagesTotalPages = resp.data.totalPages
                        this.totalViberMessages = resp.data.totalElements
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
                    .finally(() => {
                        this.isFetchingViber = false
                    })
            },
            fetchAllFallbackMessages() {
                if (this.isFetchingFallback) return
                this.isFetchingFallback = true
                return viberCampaignService
                    .getViberFallbacks(
                        this.getCurrentViberCampaign.id,
                        this.fallbackMessagesFilter,
                        true
                    )
                    .then((resp) => {
                        this.fallbackMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.fallbackPage - 1) * this.fallbackSize + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.fallbackMessagesTotalPages = resp.data.totalPages
                        this.totalFallbackMessages = resp.data.totalElements
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
                    .finally(() => {
                        this.isFetchingFallback = false
                    })
            },
            downloadFile() {
                viberCampaignService
                    .downloadUploadedFile(this.getCurrentViberCampaign.id)
                    .then((resp) => {
                        this.exportCsvFile(
                            this.getCurrentViberCampaign.alias + '_file_used',
                            resp.data
                        )
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
            },
            exportCSV(isFallback) {
                viberCampaignService
                    .getViberCampaignReports(this.getCurrentViberCampaign.id, isFallback)
                    .then((resp) => {
                        this.exportCsvFile(this.getCurrentViberCampaign.alias, resp.data)
                    })
                    .catch((error) => {
                        this.$emit('set-alert', error.response.data.message, 'error')
                    })
            },
            exportCsvFile(fileName, fileData) {
                const data = 'text;charset=utf-8,' + encodeURIComponent(fileData)
                const link = document.createElement('a')
                link.href = 'data:' + data
                link.download = `${fileName}.csv`

                const container = document.getElementById('export')
                container.appendChild(link)
                link.click()
            },
            updateViberFilter(newFilter) {
                this.viberFilter = newFilter
                this.viberPage = 1
                this.fetchAllViberMessages()
            },
            updateFallbackFilter(newFilter) {
                this.fallbackFilter = newFilter
                this.fallbackPage = 1
                this.fetchAllFallbackMessages()
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            }
        }
    }
</script>

<style scoped>
.overview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-container {
    display: flex;
    column-gap: 10px;
}

.table-container {
    flex-grow: 1;
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
}

.fallback-icon {
    text-align: center;
    padding: 5px;
    width: 50px;
}

.table-wrapper {
    padding-bottom: 1rem;
    width: 100%;
}

@media screen and (max-width: 992px) {
    .info-container {
        row-gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
